import React, { FC, useCallback, useEffect, useMemo } from 'react';

import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { CircularProgress } from '@mui/material';
import { closeModal, openModal } from 'store/portalModalDialogSlice';
import { sanitizeSlug } from 'utils/portalUtils';
import { z } from 'zod';

import { usePages } from '../../../api/portal/usePagesApi';
import DatePicker from '../../../components/DatePicker/DatePicker';
import { PORTAL_MODAL_NAMES } from '../../../components/DialogModalManager/constants/modalConfigs';
import FormHeader from '../../../components/FormHeader/FormHeader';
import Input from '../../../components/Input/Input';
import InputSelectDropdown from '../../../components/InputSelectDropdown/InputSelectDropdown';
import { cn } from '../../../components/lib/utils';
import RichText from '../../../components/RichText/RichText';
import Switch from '../../../components/Switch/Switch';
import Textarea from '../../../components/Textarea/Textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from '../../../components/ui';
import UploadImage from '../../../components/UploadImage/UploadImage';
import { EToasterStatus, showToast } from '../../../utils/showToast';
import { PagesFormProps } from '../types';

import {
  EPagesRadioButtonOptions,
  EPagesTemplateOptions,
  EPagesVisibilityOptions,
  FORM_SCHEMA,
  PAGES_RADIO_BUTTON_OPTIONS,
  pageTemplateOptions,
  visibilityOptions
} from './constants';

const PagesForm: FC<PagesFormProps> = ({ edit = false, dup = false }) => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const { addPage, updatePage, getPage, getPages } = usePages(pageId);

  const dispatch = useDispatch();

  const DEFAULT_VALUES = useMemo(
    () => ({
      pageName: '',
      pageIcon: '',
      slug: '',
      pageTemplate: EPagesTemplateOptions.CONTENT,
      priority: 0,
      visibility: EPagesVisibilityOptions.PUBLIC,
      metadataImage: '',
      metadataDescription: '',
      state: EPagesRadioButtonOptions.HIDDEN,
      publishDate: '',
      isPublishDateEnabled: false
    }),
    []
  );

  const BUTTON_TEXT = edit ? 'Update' : 'Save';

  const form = useForm<z.infer<typeof FORM_SCHEMA>>({
    resolver: zodResolver(FORM_SCHEMA),
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES
  });

  const {
    formState: { errors, isValid, isDirty }
  } = form;

  const initializeForm = useCallback(
    (data: Partial<typeof DEFAULT_VALUES>) => {
      let isPublishDateEnabled = false;
      let state = data.state;

      if (
        (data.state === EPagesRadioButtonOptions.SCHEDULED &&
          data.publishDate) ||
        (data.state === EPagesRadioButtonOptions.HIDDEN && data.publishDate)
      ) {
        isPublishDateEnabled = true;
        if (data.state === EPagesRadioButtonOptions.SCHEDULED) {
          state = EPagesRadioButtonOptions.HIDDEN;
        }
      }

      form.reset({
        ...DEFAULT_VALUES,
        ...data,
        state,
        isPublishDateEnabled
      });

      if (isPublishDateEnabled !== form.getValues('isPublishDateEnabled')) {
        form.setValue('isPublishDateEnabled', isPublishDateEnabled);
      }
    },
    [DEFAULT_VALUES, form]
  );

  useEffect(() => {
    if (edit && getPage.data) {
      initializeForm(getPage.data);
    } else if (dup && getPage.data) {
      initializeForm({
        ...getPage.data,
        pageName: `${getPage.data.pageName}_duplicated`
      });
    }
  }, [edit, dup, getPage.data, form, initializeForm]);

  const pageNameValue = form.watch('pageName');

  const isUpdateButtonEnabled = useMemo(() => {
    if (dup) return isValid;
    return isValid && isDirty;
  }, [dup, isValid, isDirty]);

  const isPageTemplateDisabled =
    edit &&
    (form.watch('pageTemplate') === EPagesTemplateOptions.HOME ||
      form.watch('pageTemplate') === EPagesTemplateOptions.STORE);

  useEffect(() => {
    if (pageNameValue && !isPageTemplateDisabled) {
      const formattedSlug = sanitizeSlug(pageNameValue);
      form.setValue('slug', formattedSlug);
    }
  }, [form, pageNameValue, isPageTemplateDisabled]);

  const onCloseForm = () => {
    navigate('../');
  };

  const onSubmit = async (data: z.infer<typeof FORM_SCHEMA>) => {
    const { isPublishDateEnabled, ...submitData } = data;

    if (submitData.state === EPagesRadioButtonOptions.VISIBLE) {
      submitData.publishDate = new Date().toISOString();
    } else if (submitData.state === EPagesRadioButtonOptions.HIDDEN) {
      if (isPublishDateEnabled && submitData.publishDate) {
        submitData.state = EPagesRadioButtonOptions.SCHEDULED;
      } else {
        delete submitData.publishDate;
      }
    }

    if (edit && pageId && !dup) {
      updatePage.mutate(
        { form: submitData, pageId },
        {
          onSuccess: () => {
            showToast({
              message: 'Page updated successfully',
              status: EToasterStatus.SUCCESS
            });
            getPages.refetch();
            onCloseForm();
          },
          onError: () => {
            showToast({
              message: 'Error updating page',
              status: EToasterStatus.ERROR
            });
          }
        }
      );
    } else {
      addPage.mutate(submitData, {
        onSuccess: () => {
          showToast({
            message: 'Page added successfully',
            status: EToasterStatus.SUCCESS
          });
          getPages.refetch();
          onCloseForm();
        },
        onError: () => {
          showToast({
            message: 'Error adding page',
            status: EToasterStatus.ERROR
          });
        }
      });
    }
  };

  const handleFormSubmit = () => {
    form.handleSubmit(onSubmit)();
  };

  const handleOptionChange = (option: EPagesRadioButtonOptions) => {
    dispatch(
      openModal({
        currentModal: PORTAL_MODAL_NAMES.PUBLISH_PAGE,
        modalActions: {
          onConfirm: {
            label: 'Save changes',
            action: () => {
              form.setValue('state', option);
              if (option === EPagesRadioButtonOptions.HIDDEN) {
                form.setValue('publishDate', '');
              }
              dispatch(closeModal());
            }
          },
          onClose: {
            label: 'Not Now',
            action: () => {
              dispatch(closeModal());
            },
            variant: 'link'
          }
        }
      })
    );
  };

  return (
    <div className="bg-white fixed h-screen w-full top-0 left-0 z-[1000] overflow-scroll">
      {(edit || dup) && getPage.isLoading && (
        <div className="absolute w-full h-full top-0 left-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm">
          <CircularProgress />
        </div>
      )}
      <FormHeader
        title="Create Portal Page"
        buttonText={BUTTON_TEXT}
        onCloseForm={onCloseForm}
        onSubmitForm={handleFormSubmit}
        closeFormOnSubmit={false}
        onOptionChange={(option) =>
          handleOptionChange(option as EPagesRadioButtonOptions)
        }
        selectedOption={form.watch('state')}
        options={PAGES_RADIO_BUTTON_OPTIONS}
        isFormValid={isUpdateButtonEnabled}
      />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="mt-7 mb-9 w-[456px] mx-auto"
        >
          <RichText title="Page Settings" className="mb-9" />
          <FormField
            control={form.control}
            name="pageName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    isRequired
                    label="Page Name"
                    placeholder="e.g., Blog"
                    state={errors.pageName?.message ? 'error' : 'default'}
                    description={errors.pageName?.message}
                    {...field}
                    className="mb-5"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="pageIcon"
            render={({ field: { onChange, value } }) => (
              <FormItem>
                <FormControl>
                  <UploadImage
                    title="Add an icon"
                    description={`Add an icon to your page.\nImage will be scaled to 130px x 60px`}
                    buttonText="Upload"
                    onUpload={(url: string) => {
                      onChange(url);
                    }}
                    className={cn(
                      'w-full mb-5',
                      errors.pageIcon?.message ? 'mb-0' : ''
                    )}
                    isRequired
                    selectedImageUrl={value}
                  />
                </FormControl>
                <FormMessage className="mt-1 !mb-5 text-p-s text-base-destructive" />
              </FormItem>
            )}
          />
          <div className="grid grid-cols-2 gap-5 mb-9 border-b pb-[50px] border-base-sidebarBorder w-full">
            <FormField
              control={form.control}
              name="slug"
              render={({ field }) => {
                const { value, onChange, onBlur, ...rest } = field;
                return (
                  <FormItem>
                    <FormControl>
                      <Input
                        label="Portal/"
                        placeholder="e.g., blog"
                        value={value}
                        onChange={onChange}
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                          form.setValue('slug', sanitizeSlug(e.target.value), {
                            shouldDirty: true,
                            shouldValidate: true
                          });
                        }}
                        description={errors.slug?.message}
                        state={errors.slug?.message ? 'error' : 'default'}
                        {...rest}
                        disabled={isPageTemplateDisabled}
                      />
                    </FormControl>
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="pageTemplate"
              render={({ field: { onChange } }) => (
                <FormItem>
                  <FormControl>
                    <InputSelectDropdown
                      label="Page Type"
                      defaultSelected={pageTemplateOptions.find(
                        (option) => option.value === form.watch('pageTemplate')
                      )}
                      options={pageTemplateOptions}
                      onOptionChange={onChange}
                      disabled={isPageTemplateDisabled}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input type="number" label="Priority" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="visibility"
              render={({ field: { onChange } }) => (
                <FormItem>
                  <FormControl>
                    <InputSelectDropdown
                      label="Visibility"
                      defaultSelected={visibilityOptions.find(
                        (option) => option.value === form.watch('visibility')
                      )}
                      options={visibilityOptions}
                      onOptionChange={onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <RichText
            title="Metadata"
            description="Enhance your page's visibility and searchability"
            className="mb-9"
          />
          <FormField
            control={form.control}
            name="metadataImage"
            render={({ field: { onChange, value } }) => (
              <FormItem>
                <FormControl>
                  <UploadImage
                    title="Add an image"
                    description="The image will be scaled to 32px x 32px"
                    buttonText="Upload"
                    onUpload={(url: string) => {
                      onChange(url);
                    }}
                    className="w-full mb-5"
                    selectedImageUrl={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="metadataDescription"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    label="Metadata Description"
                    placeholder="Write Something..."
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {form.watch('state') === EPagesRadioButtonOptions.HIDDEN && (
            <div className="w-full mt-9">
              <RichText
                title="Schedule publication date"
                description="Set a date for your content to go live"
                className="mb-5"
              />
              <FormField
                control={form.control}
                name="isPublishDateEnabled"
                render={({ field }) => (
                  <Switch
                    key={field.value ? 'on' : 'off'}
                    className="mb-9"
                    label="Enable scheduled publishing"
                    description="Turn on scheduled publishing for this content"
                    textAlignment="right"
                    defaultChecked={field.value}
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      if (!checked) {
                        form.setValue('publishDate', '');
                      }
                    }}
                  />
                )}
              />
              <FormField
                name="publishDate"
                control={form.control}
                render={({ field }) => {
                  return (
                    <FormItem>
                      <DatePicker
                        shouldExcludePast
                        displayInUTC
                        label="Publish date"
                        infoTooltipContent="All times shown in UTC"
                        disabled={!form.watch('isPublishDateEnabled')}
                        {...field}
                      />
                    </FormItem>
                  );
                }}
              />
            </div>
          )}
        </form>
      </Form>
    </div>
  );
};

export default PagesForm;
